import * as z from 'zod';
import { IS_DEV, TEST_REAL_API } from "@/config/env"; 

// ============= HELPERS =============
const ENFORCE_VALIDATION = !IS_DEV || TEST_REAL_API;

const reqString = (msg: string) =>
  ENFORCE_VALIDATION ? z.string().min(1, msg) : z.string().optional();

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

// 🟢 ROBUST FILE TYPES (Added x-pdf and fallbacks)
const ACCEPTED_MIME_TYPES = [
  "application/pdf",
  "application/x-pdf",
  "application/acrobat",
  "image/jpeg",
  "image/jpg", 
  "image/png",
];

// 🟢 Helper to validate file type safely (Checks MIME + Extension)
const isValidFileType = (file: File | undefined) => {
  if (!file) return false;
  
  // 1. Check MIME type (if browser reports it)
  if (file.type && ACCEPTED_MIME_TYPES.includes(file.type)) return true;

  // 2. Fallback: Check extension if MIME type is missing/weird
  const name = file.name?.toLowerCase();
  return name.endsWith('.pdf') || name.endsWith('.jpg') || name.endsWith('.jpeg') || name.endsWith('.png');
};

const currentYear = new Date().getFullYear();
const maxCompletionYear = currentYear - 2;

// ============= STEP 2: QUALIFICATION SCHEMA =============
export const qualificationSchema = z.object({
  degree: reqString("Please select your degree"),
  college: reqString("Please enter your college/institute"),
  yearOfCompletion: !ENFORCE_VALIDATION
    ? z.string().optional()
    : z.string().min(1, "Required").refine((val) => {
        const year = Number(val);
        return !Number.isNaN(year) && year >= 1950 && year <= maxCompletionYear;
      }, `Year must be between 1950 and ${maxCompletionYear}`),
  yearOfExperience: !ENFORCE_VALIDATION
    ? z.string().optional()
    : z.string().min(1, "Required").refine((val) => {
        const exp = Number(val);
        return !Number.isNaN(exp) && exp >= 2 && exp <= 70;
      }, "Experience must be between 2 and 70 years"),
});

// ============= STEP 3: IDENTITY PROOF SCHEMA =============
export const identityProofSchema = z.object({
  identityProof: !ENFORCE_VALIDATION
    ? z.custom<FileList>().optional()
    : z.custom<FileList>()
        .refine((files) => files && files.length > 0, "Identity proof is required")
        .refine((files) => !files?.[0] || files[0].size <= MAX_FILE_SIZE, "File size must be less than 5MB")
        .refine((files) => !files?.[0] || isValidFileType(files[0]), "Only PDF, JPG, and PNG files are accepted"),
});

// ============= STEP 4: MEDICAL REGISTRATION SCHEMA =============
export const medicalRegistrationSchema = z.object({
  medicalRegistration: !ENFORCE_VALIDATION
    ? z.custom<FileList>().optional()
    : z.custom<FileList>()
        .refine((files) => files && files.length > 0, "Medical registration proof is required")
        .refine((files) => !files?.[0] || files[0].size <= MAX_FILE_SIZE, "File size must be less than 5MB")
        .refine((files) => !files?.[0] || isValidFileType(files[0]), "Only PDF, JPG, and PNG files are accepted"),
});

// ============= STEP 5: ESTABLISHMENT PROOF SCHEMA =============
export const establishmentProofSchema = z.object({
  // 🔥 FIX: Removed .default(false) to fix TS build error. 
  // The React Hook Form defaultValues will handle the initialization.
  isAnonymous: z.boolean(), 
  
  hospitalName: z.string().optional(),
  establishmentType: z.string().optional(),
  establishmentProof: z.custom<FileList>().optional(),
})
.superRefine((data, ctx) => {
  if (ENFORCE_VALIDATION && !data.isAnonymous) {
    // 1. Check Establishment Type
    if (!data.establishmentType) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Please select establishment type",
        path: ["establishmentType"],
      });
    }

    // 2. Check Hospital Name
    if (!data.hospitalName || data.hospitalName.trim().length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Hospital/Clinic Name is required",
        path: ["hospitalName"],
      });
    }

    // 3. Check File
    if (!data.establishmentProof || data.establishmentProof.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Establishment proof is required",
        path: ["establishmentProof"],
      });
    } else {
      const file = data.establishmentProof[0];
      // Safe checks using optional chaining
      if (file?.size > MAX_FILE_SIZE) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "File size must be less than 5MB",
          path: ["establishmentProof"],
        });
      }
      if (file && !isValidFileType(file)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Only PDF, JPG, and PNG files are accepted",
          path: ["establishmentProof"],
        });
      }
    }
  }
});

// ============= TYPE EXPORTS =============
export type QualificationData = z.infer<typeof qualificationSchema>;
export type IdentityProofData = z.infer<typeof identityProofSchema>;
export type MedicalRegistrationData = z.infer<typeof medicalRegistrationSchema>;
export type EstablishmentProofData = z.infer<typeof establishmentProofSchema>;

// ============= CONSTANTS =============
export const MEDICAL_DEGREES = [
  "MBBS", "MD", "MS", "BDS", "MDS", "BAMS", "BHMS", "BUMS", 
  "BPT", "MPT", "B.Sc Nursing", "M.Sc Nursing", "DNB", "DM", "MCh",
] as const;

export const ESTABLISHMENT_TYPES = [
  "Owner of establishment",
  "Rented at other establishment",
  "Consultant doctor",
  "Practicing at home",
] as const;